import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import apiClient from '../api/client'
import { errorMessage } from '../api/ApiError'
import EmptyState from '../components/EmptyState'
import type { LambdaLog, LineaLog, LogsResponse } from '../types/portal'

/**
 * "Logs" — el registro crudo de las Lambdas, sin salir del portal.
 *
 * Existe porque quien necesita el log no siempre tiene la consola de AWS. Un
 * supervisor investigando por qué un cliente no recibió su documento tendría
 * que pedirle a alguien con acceso que le busque el id en seis log groups.
 *
 * Buscar sin elegir Lambda cruza TODAS de una vez: es el modo con el que se
 * responde "qué le pasó a esta conversación", que no sabe de antemano dónde se
 * rompió. Es más lento y vale la pena.
 *
 * Solo Admin. Los logs traen teléfonos, nombres y contenido de mensajes de
 * clientes reales, sin el filtro por asignación que protege las conversaciones.
 */

const RANGOS = [
  { min: 15, label: '15 min' },
  { min: 60, label: '1 h' },
  { min: 360, label: '6 h' },
  { min: 1440, label: '24 h' },
]

const TONO_NIVEL: Record<LineaLog['nivel'], string> = {
  ERROR:   'border-danger-border bg-danger-light text-danger',
  WARNING: 'border-warn-border bg-warn-light text-warn',
  INFO:    'border-surface-border bg-surface text-ink-muted',
}

function hora(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleTimeString('es-PE', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

/** Una línea JSON de `shared/log.py` se muestra formateada; cualquier otra cosa
 *  —un traceback, por ejemplo— tal cual vino. */
function legible(mensaje: string): string {
  const abre = mensaje.indexOf('{')
  if (abre < 0) return mensaje
  try {
    const obj = JSON.parse(mensaje.slice(abre))
    return JSON.stringify(obj, null, 2)
  } catch {
    return mensaje
  }
}

export default function Logs() {
  // El diagnóstico enlaza acá con la búsqueda ya armada (`?nivel=ERROR&
  // minutos=180`). Sin leerlos, el enlace abriría la pantalla en su estado por
  // defecto y quien lo pulsó tendría que repetir a mano el filtro que acababa
  // de pedir.
  const [params] = useSearchParams()
  const [funciones, setFunciones] = useState<LambdaLog[]>([])
  const [fn, setFn] = useState(params.get('fn') ?? '')
  const [nivel, setNivel] = useState(params.get('nivel') ?? '')
  const [texto, setTexto] = useState(params.get('q') ?? '')
  const [minutos, setMinutos] = useState(Number(params.get('minutos')) || 60)

  const [data, setData] = useState<LogsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [abierta, setAbierta] = useState<string | null>(null)

  useEffect(() => {
    apiClient.get<{ functions: LambdaLog[] }>('/logs/functions')
      .then((r) => setFunciones(r.data.functions ?? []))
      .catch(() => { /* el desplegable queda vacío; se puede buscar en todas */ })
  }, [])

  // Abrir la pantalla ya busca los errores de la última hora.
  //
  // Antes esperaba a que alguien pulsara "Buscar" y lo primero que se veía era
  // un vacío — que se lee como "no hay logs", cuando lo que pasaba es que
  // todavía no se había preguntado. Quien entra acá viene a ver qué se rompió;
  // esa es la consulta por defecto.
  const buscadoAlAbrir = useRef(false)
  useEffect(() => {
    if (buscadoAlAbrir.current) return
    buscadoAlAbrir.current = true
    // Con parámetros en la URL manda el enlace; sin ellos, los errores de la
    // última hora, que es a lo que se entra.
    const conUrl = params.get('nivel') || params.get('q') || params.get('fn')
    if (!conUrl) setNivel('ERROR')
    void buscarCon({
      fn: params.get('fn') ?? '',
      nivel: params.get('nivel') ?? (conUrl ? '' : 'ERROR'),
      texto: params.get('q') ?? '',
      minutos: Number(params.get('minutos')) || 60,
    })
    // Solo al montar: después manda lo que el usuario elija en el formulario.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** Búsqueda con parámetros explícitos: la de apertura no puede leerlos del
   *  estado, que en ese render todavía tiene los valores iniciales. */
  const buscarCon = useCallback(async (p: {
    fn?: string; nivel?: string; texto?: string; minutos?: number
  }) => {
    setLoading(true)
    setError(null)
    try {
      const q = new URLSearchParams()
      if (p.fn) q.set('fn', p.fn)
      if (p.nivel) q.set('nivel', p.nivel)
      if (p.texto?.trim()) q.set('q', p.texto.trim())
      q.set('minutos', String(p.minutos ?? 60))
      const res = await apiClient.get<LogsResponse>(`/logs?${q}`)
      setData(res.data)
    } catch (err) {
      setError(errorMessage(err, 'No se pudieron leer los logs.'))
    } finally {
      setLoading(false)
    }
  }, [])

  const buscar = useCallback(
    () => buscarCon({ fn, nivel, texto, minutos }),
    [buscarCon, fn, nivel, texto, minutos])

  return (
    <div className="flex flex-col gap-4">
      <form
        className="flex flex-wrap items-end gap-2.5 rounded-card border border-surface-border bg-white p-4"
        onSubmit={(e) => { e.preventDefault(); void buscar() }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-meta font-bold uppercase tracking-[.06em] text-ink-faint">Lambda</span>
          <select
            value={fn}
            onChange={(e) => setFn(e.target.value)}
            className="rounded-input border border-surface-border bg-white px-3 py-2 text-body text-ink focus:border-brand focus:outline-none"
          >
            <option value="">Todas</option>
            {funciones.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-meta font-bold uppercase tracking-[.06em] text-ink-faint">Nivel</span>
          <select
            value={nivel}
            onChange={(e) => setNivel(e.target.value)}
            className="rounded-input border border-surface-border bg-white px-3 py-2 text-body text-ink focus:border-brand focus:outline-none"
          >
            <option value="">Todos</option>
            <option value="ERROR">Solo errores</option>
            <option value="WARNING">Avisos</option>
            <option value="INFO">Informativos</option>
          </select>
        </label>

        <label className="flex min-w-[240px] flex-1 flex-col gap-1">
          <span className="text-meta font-bold uppercase tracking-[.06em] text-ink-faint">
            Buscar texto o ID de conversación
          </span>
          <input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="p. ej. 0cddc8b5-2a97…"
            className="rounded-input border border-surface-border px-3 py-2 font-mono text-body text-ink placeholder:font-sans placeholder:text-ink-faint focus:border-brand focus:outline-none"
          />
        </label>

        <div className="flex flex-col gap-1">
          <span className="text-meta font-bold uppercase tracking-[.06em] text-ink-faint">Rango</span>
          <div className="flex gap-1">
            {RANGOS.map((r) => (
              <button
                key={r.min}
                type="button"
                onClick={() => setMinutos(r.min)}
                className={minutos === r.min ? 'pill-filter-active' : 'pill-filter'}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="rounded-input bg-brand px-5 py-2.5 text-body font-bold text-white transition-colors hover:bg-brand-hover disabled:opacity-50"
        >
          {loading ? 'Buscando…' : 'Buscar'}
        </button>
      </form>

      {error && (
        <p className="rounded-card border border-danger-border bg-danger-light px-4 py-3 text-body text-danger">
          {error}
        </p>
      )}

      {!data && !loading && !error && (
        <EmptyState
          icon="📋"
          title="Elige qué mirar"
          description="Sin Lambda seleccionada busca en todas: es más lento, y es lo que hace falta cuando no se sabe dónde se rompió."
        />
      )}

      {data && (
        <div className="rounded-card border border-surface-border bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-surface-border px-5 py-3">
            <p className="text-body text-ink-muted">
              <b className="text-ink">{data.count}</b> línea{data.count === 1 ? '' : 's'}
              {' '}· {data.consulta.funcion} · últimos {data.consulta.minutos} min
              {data.truncado && ' · hay más, acota la búsqueda'}
            </p>
            <button className="btn-ghost" onClick={() => void buscar()}>Actualizar</button>
          </div>

          {data.lineas.length === 0 ? (
            <p className="px-5 py-10 text-center text-body text-ink-faint">
              Nada coincide. Prueba a ampliar el rango o quitar el filtro de nivel.
            </p>
          ) : (
            <ul className="divide-y divide-surface-faint">
              {data.lineas.map((l, i) => {
                const clave = `${l.timestamp}-${i}`
                const abiertaEsta = abierta === clave
                return (
                  <li key={clave}>
                    <button
                      onClick={() => setAbierta(abiertaEsta ? null : clave)}
                      className="flex w-full items-start gap-3 px-5 py-2.5 text-left transition-colors hover:bg-surface"
                    >
                      <span className="mt-0.5 shrink-0 font-mono text-micro tabular-nums text-ink-faint">
                        {hora(l.timestamp)}
                      </span>
                      <span className={`mt-0.5 shrink-0 rounded-chip border px-1.5 py-0.5 text-pill font-bold ${TONO_NIVEL[l.nivel]}`}>
                        {l.nivel}
                      </span>
                      <span className="mt-0.5 shrink-0 text-pill text-ink-muted">
                        {l.funcion_label}
                      </span>
                      <span className={`min-w-0 flex-1 font-mono text-micro text-ink ${abiertaEsta ? '' : 'truncate'}`}>
                        {abiertaEsta ? '' : l.mensaje}
                      </span>
                    </button>
                    {abiertaEsta && (
                      <pre className="mx-5 mb-3 max-h-[420px] overflow-auto rounded-card border border-surface-faint bg-surface-alt p-3.5 font-mono text-micro leading-relaxed text-ink">
{legible(l.mensaje)}
                      </pre>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
